import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconAlertCircle,
  IconCheck,
  IconClockHour4,
  IconDatabase,
  IconInfoCircle,
  IconPlayerPlay,
  IconPlus,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ProgressSegmented } from '@/components/ui/progress';
import { RingProgress } from '@/components/ui/ring-progress';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip } from '@/components/ui/tooltip';
import { formatBytes, relativeTime } from '@/lib/utils';
import { CollapsibleSection } from '../CollapsibleSection.js';
import {
  api,
  UnauthorizedError,
  IGNORE_PRESET_OPTIONS,
  type AdminAction,
  type JobsSummary,
  type IgnorePreset,
} from '../api.js';
import { AdminBackupsSection } from './AdminBackups.js';
import { AdminColdStorageSection } from './AdminColdStorage.js';
import { AdminTagsSection } from './AdminTags.js';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// Reused badge tone vocabulary — same as Badge's `tone` variants.
type Tone = 'neutral' | 'orange' | 'green' | 'red' | 'blue' | 'yellow';

// Admin home. Each section is collapsible with localStorage-persisted
// open state via CollapsibleSection. New sections drop in by adding
// another <CollapsibleSection>.
export function AdminPage() {
  return (
    <div className="mx-auto max-w-7xl">
      <h1 className="mb-4 text-xl font-semibold tracking-tight">Admin</h1>
      <div className="flex flex-col gap-1">
        <CollapsibleSection id="admin.render-queue" title="Render queue">
          <RenderJobsSection />
        </CollapsibleSection>
        <CollapsibleSection id="admin.storage" title="Storage">
          <StorageSection />
        </CollapsibleSection>
        <CollapsibleSection id="admin.backups" title="Backups">
          <AdminBackupsSection />
        </CollapsibleSection>
        <CollapsibleSection id="admin.cold-storage" title="Cold storage">
          <AdminColdStorageSection />
        </CollapsibleSection>
        <CollapsibleSection id="admin.tags" title="Tags" defaultOpen={false}>
          <AdminTagsSection />
        </CollapsibleSection>
        <CollapsibleSection
          id="admin.create-depot"
          title="Create depot"
          defaultOpen={false}
        >
          <CreateDepotSection />
        </CollapsibleSection>
        <CollapsibleSection
          id="admin.add-stream"
          title="Add stream"
          defaultOpen={false}
        >
          <AddStreamSection />
        </CollapsibleSection>
        <CollapsibleSection
          id="admin.recent-actions"
          title="Recent admin actions"
          defaultOpen={false}
        >
          <RecentAdminActionsSection />
        </CollapsibleSection>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Render queue section
// ---------------------------------------------------------------------------

function RenderJobsSection() {
  const query = useQuery({
    queryKey: ['admin.jobs.summary'],
    queryFn: () => api.adminJobsSummary(),
    refetchInterval: 5000,
  });

  if (query.error instanceof UnauthorizedError) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertTitle>Not signed in</AlertTitle>
        <AlertDescription>Sign in to view admin tools.</AlertDescription>
      </Alert>
    );
  }
  if (query.error) {
    const msg = query.error.message;
    if (/admin only/i.test(msg)) {
      return (
        <Alert tone="warning">
          <IconInfoCircle />
          <AlertTitle>Admin access required</AlertTitle>
          <AlertDescription>
            The render-job panel is restricted to Perforce users with
            super/admin protections.
          </AlertDescription>
        </Alert>
      );
    }
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>{msg}</AlertDescription>
      </Alert>
    );
  }
  if (!query.data) return <Spinner />;

  const { counts, completedLastHour, running, recentFailures } = query.data;
  const total = counts.queued + counts.running + counts.done + counts.failed;
  const pct = total === 0 ? 0 : Math.round((counts.done / total) * 100);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-4">
        <Card className="min-w-[240px]">
          <CardContent className="flex items-center gap-4 py-4">
            <RingProgress
              size={80}
              thickness={8}
              sections={[
                { value: total === 0 ? 0 : (counts.done / total) * 100,    color: 'var(--color-green-500)' },
                { value: total === 0 ? 0 : (counts.running / total) * 100, color: 'var(--color-blue-500)' },
                { value: total === 0 ? 0 : (counts.queued / total) * 100,  color: 'var(--color-neutral-500)' },
                { value: total === 0 ? 0 : (counts.failed / total) * 100,  color: 'var(--color-red-500)' },
              ]}
              label={
                <span className="text-sm font-bold tabular-nums">{pct}%</span>
              }
            />
            <div className="flex flex-col gap-0.5 text-xs">
              <span className="text-muted-foreground">
                Of {total.toLocaleString()} jobs
              </span>
              <CountLine tone="green" label="done" n={counts.done} />
              <CountLine tone="blue" label="running" n={counts.running} />
              <CountLine tone="neutral" label="queued" n={counts.queued} />
              <CountLine tone="red" label="failed" n={counts.failed} />
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-[200px]">
          <CardContent className="flex flex-col gap-1 py-4">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <IconClockHour4 size={13} />
              Last hour
            </span>
            <span className="text-2xl font-bold tabular-nums">
              {completedLastHour.toLocaleString()}
            </span>
            <span className="text-xs text-muted-foreground">renders completed</span>
            {counts.queued > 0 && completedLastHour > 0 && (
              <span className="mt-1 text-xs text-muted-foreground">
                ETA to drain queue: ~
                {Math.ceil(counts.queued / (completedLastHour / 60))} min
              </span>
            )}
          </CardContent>
        </Card>

        <AdminActions />
      </div>

      {running.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <h3 className="mb-2 text-sm font-semibold">In flight</h3>
            <div className="rounded-md border border-neutral-800">
              <Table>
                <TableBody>
                  {running.map((j) => (
                    <TableRow key={j.id}>
                      <TableCell className="w-[80px]">
                        <Badge tone="blue" className="gap-0.5">
                          <IconPlayerPlay size={9} />
                          #{j.id}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-[11px]">
                        {j.depotPath}
                        <span className="text-muted-foreground"> #{j.headRev}</span>
                      </TableCell>
                      <TableCell className="w-[220px]">
                        <Tooltip label={`Started ${j.startedAt ?? '—'}`}>
                          <span className="text-[11px] text-muted-foreground">
                            {j.startedAt
                              ? new Date(j.startedAt).toLocaleTimeString()
                              : '—'}
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {recentFailures.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <h3 className="mb-2 text-sm font-semibold">Recent failures</h3>
            <div className="rounded-md border border-neutral-800">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[90px]">Job</TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead className="w-[100px]">Attempts</TableHead>
                    <TableHead>Error</TableHead>
                    <TableHead className="w-[80px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentFailures.map((j) => (
                    <FailureRow
                      key={`${j.depotPath}#${j.headRev}#${j.kind}`}
                      job={j}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CountLine({
  tone,
  label,
  n,
}: {
  tone: 'green' | 'blue' | 'neutral' | 'red';
  label: string;
  n: number;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge tone={tone}>{label}</Badge>
      <span className="tabular-nums">{n.toLocaleString()}</span>
    </span>
  );
}

function FailureRow({
  job,
}: {
  job: JobsSummary['recentFailures'][number];
}) {
  const qc = useQueryClient();
  const retry = useMutation({
    mutationFn: () => api.adminRetryAsset(job.depotPath, job.headRev, job.kind),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin.jobs.summary'] }),
  });
  const attemptsLabel =
    job.rowCount > 1
      ? `${job.totalAttempts} (×${job.rowCount} rows)`
      : String(job.totalAttempts);
  return (
    <TableRow>
      <TableCell>
        <Badge tone="red">#{job.latestId}</Badge>
      </TableCell>
      <TableCell className="font-mono text-[11px]">
        <Link
          to={`/depot${job.depotPath.replace(/^\/\//, '/')}`}
          className="text-theia-400 hover:underline"
        >
          {job.depotPath}
        </Link>
        <span className="text-muted-foreground"> #{job.headRev}</span>
      </TableCell>
      <TableCell>
        <Tooltip
          label={
            job.rowCount > 1
              ? `${job.totalAttempts} render attempts across ${job.rowCount} failed rows`
              : 'render attempts'
          }
        >
          <span className="text-xs">{attemptsLabel}</span>
        </Tooltip>
      </TableCell>
      <TableCell>
        <span
          className="line-clamp-2 text-[11px] text-muted-foreground"
          title={job.error ?? ''}
        >
          {job.error ?? '—'}
        </span>
      </TableCell>
      <TableCell>
        <Button
          variant="default"
          size="sm"
          onClick={() => retry.mutate()}
          disabled={retry.isPending}
        >
          {retry.isPending ? <Spinner size="xs" /> : <IconRefresh />}
          Retry
        </Button>
      </TableCell>
    </TableRow>
  );
}

function AdminActions() {
  const qc = useQueryClient();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const retry = useMutation({
    mutationFn: () => api.adminRetryFailedJobs(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin.jobs.summary'] }),
  });
  const cancel = useMutation({
    mutationFn: () => api.adminCancelQueuedJobs(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin.jobs.summary'] });
      setConfirmCancel(false);
    },
  });

  return (
    <Card className="min-w-[200px]">
      <CardContent className="flex flex-col gap-1.5 py-4">
        <span className="text-xs text-muted-foreground">Actions</span>
        <Button
          variant="secondary"
          onClick={() => retry.mutate()}
          disabled={retry.isPending}
        >
          {retry.isPending ? <Spinner size="xs" /> : <IconRefresh />}
          Retry failed jobs
        </Button>
        <Button
          variant="secondary"
          onClick={() => setConfirmCancel(true)}
          className="text-red-300 hover:text-red-200 [&_svg]:text-red-400"
        >
          <IconTrash />
          Cancel queued
        </Button>
        {retry.data && (
          <span className="text-xs text-muted-foreground">
            Retried {retry.data.retried} jobs
          </span>
        )}
        {cancel.data && (
          <span className="text-xs text-muted-foreground">
            Canceled {cancel.data.canceled} jobs
          </span>
        )}
      </CardContent>

      <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel all queued jobs?</DialogTitle>
            <DialogDescription>
              This deletes every dam_job row with state = "queued". Running
              jobs finish naturally; done / failed history is preserved.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmCancel(false)}>
              Keep them
            </Button>
            <Button
              variant="destructive"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
            >
              {cancel.isPending && <Spinner size="xs" />}
              Yes, cancel queued
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Storage section
// ---------------------------------------------------------------------------

function StorageSection() {
  const qc = useQueryClient();
  // Storage isn't volatile — the underlying snapshot only changes
  // every 5 min server-side, and disk-pressure trends move on the
  // scale of days, not seconds. Daily polling is plenty; the
  // Recompute button is the escape hatch for "I want to see now".
  const summary = useQuery({
    queryKey: ['admin.storage.summary'],
    queryFn: () => api.adminStorageSummary(),
    refetchInterval: 24 * 60 * 60_000,
  });
  const topAssets = useQuery({
    queryKey: ['admin.storage.top-assets'],
    queryFn: () => api.adminStorageTopAssets(10),
    refetchInterval: 24 * 60 * 60_000,
  });

  if (summary.error instanceof UnauthorizedError) return null;
  if (summary.error) {
    const msg = summary.error.message;
    if (/admin only/i.test(msg)) return null;
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>{msg}</AlertDescription>
      </Alert>
    );
  }
  if (!summary.data) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 py-4">
          <Spinner size="xs" />
          <span className="text-xs text-muted-foreground">Loading storage…</span>
        </CardContent>
      </Card>
    );
  }

  const { filesystem, damCache } = summary.data;
  const perforce = summary.data.perforce ?? {
    p4dDataBytes: 0,
    p4dDataFiles: 0,
    checkpointsBytes: 0,
    checkpointsFiles: 0,
    totalBytes: 0,
  };
  const pctUsed =
    filesystem.totalBytes === 0
      ? 0
      : Math.round((filesystem.usedBytes / filesystem.totalBytes) * 100);

  const pressureTone: 'green' | 'yellow' | 'red' =
    pctUsed >= 90 ? 'red' : pctUsed >= 70 ? 'yellow' : 'green';

  const perforceUsedBytes = perforce.totalBytes;
  const otherUsedBytes = Math.max(0, filesystem.usedBytes - perforceUsedBytes);
  const pctPerforce =
    filesystem.totalBytes === 0
      ? 0
      : (perforceUsedBytes / filesystem.totalBytes) * 100;
  const pctOther =
    filesystem.totalBytes === 0
      ? 0
      : (otherUsedBytes / filesystem.totalBytes) * 100;

  const pressureSwatch =
    pressureTone === 'red'
      ? 'bg-red-500'
      : pressureTone === 'yellow'
        ? 'bg-yellow-500'
        : 'bg-green-500';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start gap-4">
        <Card className="min-w-[360px] max-w-[520px] flex-1">
          <CardContent className="flex flex-col gap-2 py-4">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <IconDatabase size={13} />
              <span className="uppercase tracking-wider font-medium">Disk</span>
              <Tooltip
                label={`Mount: ${filesystem.mountPath} (Colima VM — shared with all containers and the host macOS install)`}
              >
                <span className="cursor-help">ⓘ</span>
              </Tooltip>
            </span>
            <div className="flex items-baseline justify-between">
              <span className="text-xl font-bold tabular-nums">
                {formatBytes(filesystem.usedBytes)}
              </span>
              <span className="text-xs text-muted-foreground">
                of {formatBytes(filesystem.totalBytes)} ({pctUsed}%)
              </span>
            </div>
            <ProgressSegmented
              segments={[
                {
                  value: pctPerforce,
                  tone: pressureTone,
                  wrap: (n) => (
                    <Tooltip
                      label={`Perforce stack: ${formatBytes(perforceUsedBytes)}`}
                    >
                      {n as React.ReactElement}
                    </Tooltip>
                  ),
                },
                {
                  value: pctOther,
                  tone: 'neutral',
                  wrap: (n) => (
                    <Tooltip
                      label={`Other host usage (macOS + other apps): ${formatBytes(otherUsedBytes)}`}
                    >
                      {n as React.ReactElement}
                    </Tooltip>
                  ),
                },
              ]}
            />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <span className={`block h-2 w-2 rounded-sm ${pressureSwatch}`} />
                Perforce {formatBytes(perforceUsedBytes)}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="block h-2 w-2 rounded-sm bg-neutral-500" />
                Other {formatBytes(otherUsedBytes)}
              </span>
              <span>· {formatBytes(filesystem.freeBytes)} free</span>
            </div>
          </CardContent>
        </Card>

        <Card className="min-w-[300px]">
          <CardContent className="flex flex-col gap-1 py-4">
            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Perforce footprint
            </span>
            <span className="text-xl font-bold tabular-nums">
              {formatBytes(perforce.totalBytes)}
            </span>
            <table className="mt-1 text-xs">
              <tbody>
                <FootprintRow
                  label="p4d data"
                  bytes={perforce.p4dDataBytes}
                  meta={`${perforce.p4dDataFiles.toLocaleString()} files`}
                />
                <FootprintRow
                  label="checkpoints"
                  bytes={perforce.checkpointsBytes}
                  meta={`${perforce.checkpointsFiles.toLocaleString()} files`}
                />
                <FootprintRow
                  label="dam-cache"
                  bytes={damCache.bytes}
                  meta={`${damCache.assetDirs.toLocaleString()} asset rev${damCache.assetDirs === 1 ? '' : 's'}`}
                />
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="min-w-[180px]">
          <CardContent className="flex flex-col gap-1.5 py-4">
            <span className="text-xs text-muted-foreground">Actions</span>
            <Button
              variant="secondary"
              onClick={async () => {
                await api.adminStorageSummary(true);
                qc.invalidateQueries({ queryKey: ['admin.storage.summary'] });
                qc.invalidateQueries({ queryKey: ['admin.storage.top-assets'] });
              }}
              disabled={summary.isFetching || topAssets.isFetching}
            >
              {summary.isFetching || topAssets.isFetching ? (
                <Spinner size="xs" />
              ) : (
                <IconRefresh />
              )}
              Recompute
            </Button>
            <span className="text-xs text-muted-foreground">
              Snapshot age:{' '}
              {Math.max(
                0,
                Math.round(
                  (Date.now() - new Date(summary.data.takenAt).getTime()) /
                    1000,
                ),
              )}
              s
            </span>
          </CardContent>
        </Card>
      </div>

      {topAssets.data && topAssets.data.assets.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <h3 className="mb-2 text-sm font-semibold">Biggest cached assets</h3>
            <div className="rounded-md border border-neutral-800">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[110px]">Size</TableHead>
                    <TableHead>Asset</TableHead>
                    <TableHead className="w-[120px]">Revs</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topAssets.data.assets.map((a) => (
                    <TableRow key={a.depotPath}>
                      <TableCell className="font-medium tabular-nums">
                        {formatBytes(a.bytes)}
                      </TableCell>
                      <TableCell className="font-mono text-[11px]">
                        <Link
                          to={`/depot${a.depotPath.replace(/^\/\//, '/')}`}
                          className="text-theia-400 hover:underline"
                        >
                          {a.depotPath}
                        </Link>
                      </TableCell>
                      <TableCell className="text-xs">
                        {a.revs} {a.revs === 1 ? 'rev' : 'revs'}
                        {a.revs > 1 && (
                          <span className="text-muted-foreground">
                            {' '}(latest #{a.latestRev})
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FootprintRow({
  label,
  bytes,
  meta,
}: {
  label: string;
  bytes: number;
  meta?: string;
}) {
  return (
    <tr>
      <td className="py-0.5 text-muted-foreground">{label}</td>
      <td className="py-0.5 pl-3 text-right font-medium tabular-nums">
        {formatBytes(bytes)}
      </td>
      {meta && (
        <td className="py-0.5 pl-3 text-muted-foreground">{meta}</td>
      )}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Create Depot section
// ---------------------------------------------------------------------------

// Depot name shape: same regex the backend enforces. Mirrored here so
// the form can validate inline without round-tripping.
const DEPOT_NAME_RE = /^[A-Za-z][A-Za-z0-9_\-.]{0,63}$/;

function CreateDepotSection() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [ownerGroup, setOwnerGroup] = useState('');
  const [streamDepth, setStreamDepth] = useState('1');
  const [ignorePreset, setIgnorePreset] = useState<IgnorePreset>('ue5');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      api.adminCreateDepot({
        name,
        description,
        ...(ownerGroup ? { ownerGroup } : {}),
        streamDepth: parseInt(streamDepth, 10) || 1,
        ignorePreset,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin.actions'] });
      setConfirmOpen(false);
      setName('');
      setDescription('');
      setOwnerGroup('');
    },
  });

  const nameValid = DEPOT_NAME_RE.test(name);
  const ready = nameValid && description.trim().length > 0;
  const depth = parseInt(streamDepth, 10);
  const stream = nameValid ? `//${name}/Main` : '//<name>/Main';

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        Bootstraps a new project end-to-end: stream depot, mainline stream
        with the canonical <code>Ignored:</code> patterns pre-populated,
        and an initial <code>.p4ignore</code> submit. Admin-only.
      </p>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="depot-name">Depot name</Label>
        <Input
          id="depot-name"
          placeholder="NewGame_UE5"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          className={
            name.length > 0 && !nameValid ? 'border-red-700' : undefined
          }
        />
        <span className="text-[11px] text-muted-foreground">
          Letters, digits, dash, dot, underscore. Starts with a letter.
          Becomes <code className="font-mono">{stream}</code>.
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="depot-desc">Description</Label>
        <Input
          id="depot-desc"
          placeholder="e.g. Mainline depot for NewGame UE5 project."
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="depot-owner-group">Owner group (optional)</Label>
          <Input
            id="depot-owner-group"
            placeholder="p4 group name"
            value={ownerGroup}
            onChange={(e) => setOwnerGroup(e.currentTarget.value)}
          />
          <span className="text-[11px] text-muted-foreground">
            Falls back to your user if blank.
          </span>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="depot-depth">Stream depth</Label>
          <Input
            id="depot-depth"
            type="number"
            min={1}
            max={4}
            value={streamDepth}
            onChange={(e) => setStreamDepth(e.currentTarget.value)}
          />
          <span className="text-[11px] text-muted-foreground">
            Default 1 (e.g. <code>//name/main</code>).
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="depot-ignore-preset">Ignore patterns</Label>
        <select
          id="depot-ignore-preset"
          value={ignorePreset}
          onChange={(e) => setIgnorePreset(e.currentTarget.value as IgnorePreset)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          {IGNORE_PRESET_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-muted-foreground">
          {ignorePreset === 'none' ? (
            <>
              Leaves the mainline's <code>Ignored:</code> field empty and
              submits no <code>.p4ignore</code> — manage ignores by hand.
            </>
          ) : ignorePreset === 'roblox' ? (
            <>
              Seeds the Roblox set (<code>Packages/</code>,{' '}
              <code>node_modules/</code>, <code>out/</code>,{' '}
              <code>sourcemap.json</code>, Studio <code>*.lock</code>) into{' '}
              <code>Ignored:</code> and submits a Roblox{' '}
              <code>.p4ignore</code>. Place files (<code>.rbxl</code>) are
              kept — see the .p4ignore comment to ignore them for Rojo.
            </>
          ) : (
            <>
              Seeds the UE5 set (<code>*_BuiltData.uasset</code>,{' '}
              <code>Saved/</code>, <code>Intermediate/</code>,{' '}
              <code>Binaries/</code>, …) into <code>Ignored:</code> and
              submits the full UE <code>.p4ignore</code> at the stream root.
            </>
          )}
        </span>
      </div>

      <div>
        <Button
          variant="primary"
          disabled={!ready || create.isPending}
          onClick={() => setConfirmOpen(true)}
        >
          {create.isPending ? <Spinner size="xs" /> : <IconPlus />}
          Create depot
        </Button>
      </div>

      {create.error && (
        <Alert tone="destructive">
          <IconAlertCircle />
          <AlertDescription>{(create.error as Error).message}</AlertDescription>
        </Alert>
      )}

      {create.data && (
        <Alert tone="default">
          <IconCheck />
          <AlertTitle>Depot created</AlertTitle>
          <AlertDescription>
            <span className="font-mono">{create.data.stream}</span>
            {create.data.initialChange > 0 ? (
              <>
                {' '}@ change{' '}
                <Link
                  to={`/changes/${create.data.initialChange}`}
                  className="text-theia-400 hover:underline"
                >
                  #{create.data.initialChange}
                </Link>
              </>
            ) : (
              <> (no <code>.p4ignore</code> submitted)</>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create depot {name}?</DialogTitle>
            <DialogDescription>
              This creates the depot and a <code>Main</code> mainline stream
              at <span className="font-mono">{`//${name}/`}</span> with
              stream depth {Number.isFinite(depth) && depth > 0 ? depth : 1}.
              {ignorePreset === 'none' ? (
                <>
                  {' '}Ignore patterns will NOT be applied — the mainline's{' '}
                  <code>Ignored:</code> field is empty and no{' '}
                  <code>.p4ignore</code> is submitted. You'll be responsible
                  for adding them later.
                </>
              ) : (
                <>
                  {' '}The{' '}
                  <span className="font-medium">
                    {ignorePreset === 'roblox' ? 'Roblox' : 'UE5'}
                  </span>{' '}
                  <code>Ignored:</code> patterns are pre-populated and the
                  initial <code>.p4ignore</code> is submitted under your
                  account.
                </>
              )}{' '}
              Best-effort rollback on failure — if rollback itself fails,
              you may need to clean up via P4Admin.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              {create.isPending && <Spinner size="xs" />}
              Confirm + create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Stream section
// ---------------------------------------------------------------------------

// Same single-segment regex the backend uses for stream names.
const STREAM_NAME_RE = /^[A-Za-z][A-Za-z0-9_\-.]{0,63}$/;

type StreamType = 'mainline' | 'development' | 'release';

function AddStreamSection() {
  const qc = useQueryClient();
  const [depot, setDepot] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<StreamType>('development');
  const [description, setDescription] = useState('');
  const [parent, setParent] = useState('');
  const [populate, setPopulate] = useState(false);
  const [ignorePreset, setIgnorePreset] = useState<IgnorePreset>('ue5');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const depotsQ = useQuery({
    queryKey: ['admin.stream-depots'],
    queryFn: () => api.adminStreamDepots(),
  });
  const streamsQ = useQuery({
    queryKey: ['admin.streams', depot],
    queryFn: () => api.adminStreamsInDepot(depot),
    enabled: depot.length > 0,
  });

  const create = useMutation({
    mutationFn: () =>
      api.adminCreateStream({
        depot,
        name,
        type,
        description,
        ignorePreset,
        ...(parent ? { parent } : {}),
        ...(type !== 'mainline' && populate ? { populate: true } : {}),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin.actions'] });
      qc.invalidateQueries({ queryKey: ['admin.streams', depot] });
      setConfirmOpen(false);
      setName('');
      setDescription('');
      setParent('');
    },
  });

  const nameValid = STREAM_NAME_RE.test(name);
  const ready =
    depot.length > 0 &&
    nameValid &&
    description.trim().length > 0 &&
    (type === 'mainline' || parent.length > 0 || autoParentAvailable(streamsQ.data?.streams));
  const streamPath = depot && nameValid ? `//${depot}/${name}` : '//<depot>/<name>';

  // For non-mainline streams: if exactly one mainline exists in the
  // depot, auto-fill it as the parent (operator can override). If
  // there are several mainlines, force explicit selection.
  const candidateParents = (streamsQ.data?.streams ?? []).filter(
    (s) => s.type !== 'task' && s.stream !== streamPath,
  );

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <p className="text-xs text-muted-foreground">
        Adds a development, release, or additional-mainline stream to an
        existing depot. Inherits views + content from the parent; no
        bootstrap submit needed since the parent's <code>.p4ignore</code>
        propagates. Optional <code>p4 populate</code> seeds the new
        stream with the parent's files in one server-side copy.
      </p>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="add-stream-depot">Depot</Label>
        {depotsQ.isLoading ? (
          <Spinner size="xs" />
        ) : depotsQ.error ? (
          <span className="text-xs text-red-400">
            Failed to load depots: {(depotsQ.error as Error).message}
          </span>
        ) : (
          <select
            id="add-stream-depot"
            value={depot}
            onChange={(e) => {
              setDepot(e.currentTarget.value);
              setParent('');
            }}
            className="h-7 rounded-sm border border-neutral-800 bg-neutral-950 px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select…</option>
            {depotsQ.data?.depots.map((d) => (
              <option key={d.name} value={d.name}>{d.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="add-stream-name">Stream name</Label>
        <Input
          id="add-stream-name"
          placeholder="Dev"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          className={name.length > 0 && !nameValid ? 'border-red-700' : undefined}
        />
        <span className="text-[11px] text-muted-foreground">
          Single segment, PascalCase by convention. Becomes{' '}
          <code className="font-mono">{streamPath}</code>.
        </span>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label>Type</Label>
        <div role="radiogroup" className="flex gap-3">
          {(['development', 'release', 'mainline'] as const).map((t) => (
            <label
              key={t}
              className="inline-flex cursor-pointer items-center gap-1.5 text-xs"
            >
              <input
                type="radio"
                name="add-stream-type"
                value={t}
                checked={type === t}
                onChange={() => setType(t)}
                className="accent-theia-500"
              />
              {t}
            </label>
          ))}
        </div>
        <span className="text-[11px] text-muted-foreground">
          {type === 'mainline'
            ? 'Mainline: no parent. Rare second mainline within a depot.'
            : type === 'development'
              ? 'Development: child of mainline (or another dev). Submits flow up + down through the parent.'
              : 'Release: child of mainline. Cuts a release branch; usually merge-only from parent.'}
        </span>
      </div>

      {type !== 'mainline' && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="add-stream-parent">Parent stream</Label>
          <select
            id="add-stream-parent"
            value={parent}
            onChange={(e) => setParent(e.currentTarget.value)}
            disabled={!depot}
            className="h-7 rounded-sm border border-neutral-800 bg-neutral-950 px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
          >
            <option value="">
              {autoParentAvailable(streamsQ.data?.streams)
                ? '(auto: depot mainline)'
                : 'Select…'}
            </option>
            {candidateParents.map((s) => (
              <option key={s.stream} value={s.stream}>
                {s.stream} ({s.type})
              </option>
            ))}
          </select>
          <span className="text-[11px] text-muted-foreground">
            Defaults to the depot's mainline when there's exactly one;
            pick explicitly otherwise.
          </span>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="add-stream-desc">Description</Label>
        <Input
          id="add-stream-desc"
          placeholder="e.g. Active development branch for the next milestone."
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
      </div>

      {type !== 'mainline' && (
        <label className="inline-flex items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={populate}
            onChange={(e) => setPopulate(e.currentTarget.checked)}
            className="mt-0.5 accent-theia-500"
          />
          <span>
            Populate from parent immediately ({' '}
            <code className="font-mono">p4 populate</code>
            ). Adds every parent file to the new stream in one changelist.
            Leave unchecked to create an empty stream that team members
            populate later.
          </span>
        </label>
      )}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="stream-ignore-preset">Ignore patterns</Label>
        <select
          id="stream-ignore-preset"
          value={ignorePreset}
          onChange={(e) => setIgnorePreset(e.currentTarget.value as IgnorePreset)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          {IGNORE_PRESET_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="text-[11px] text-muted-foreground">
          Seeds the new stream's <code>Ignored:</code> field with the{' '}
          {ignorePreset === 'none'
            ? 'nothing (left empty)'
            : ignorePreset === 'roblox'
              ? 'Roblox set (Packages/, node_modules/, sourcemap.json, …)'
              : 'UE5 set (*_BuiltData.uasset, Saved/, Intermediate/, …)'}
          . The parent's <code>Ignored:</code> doesn't inherit, so we
          re-apply per-stream; the parent's <code>.p4ignore</code> file is
          inherited via the view either way.
        </span>
      </div>

      <div>
        <Button
          variant="primary"
          disabled={!ready || create.isPending}
          onClick={() => setConfirmOpen(true)}
        >
          {create.isPending ? <Spinner size="xs" /> : <IconPlus />}
          Add stream
        </Button>
      </div>

      {create.error && (
        <Alert tone="destructive">
          <IconAlertCircle />
          <AlertDescription>{(create.error as Error).message}</AlertDescription>
        </Alert>
      )}
      {create.data && (
        <Alert tone="default">
          <IconCheck />
          <AlertTitle>Stream created</AlertTitle>
          <AlertDescription>
            <span className="font-mono">{create.data.stream}</span>
            {create.data.populatedChange !== null && (
              <>
                {' '}@ populate change{' '}
                <Link
                  to={`/changes/${create.data.populatedChange}`}
                  className="text-theia-400 hover:underline"
                >
                  #{create.data.populatedChange}
                </Link>
              </>
            )}
          </AlertDescription>
        </Alert>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add stream {streamPath}?</DialogTitle>
            <DialogDescription>
              Creates a {type} stream{' '}
              <span className="font-mono">{streamPath}</span>
              {type !== 'mainline' && (
                <>
                  {' '}with parent{' '}
                  <span className="font-mono">
                    {parent || '(auto-selected mainline)'}
                  </span>
                </>
              )}
              .
              {type !== 'mainline' && populate && (
                <> Will also <code>p4 populate</code> from the parent — every parent file copies into the new stream.</>
              )}
              {ignorePreset === 'none' ? (
                <> Ignore patterns will NOT be applied — the new stream's <code>Ignored:</code> field is empty.</>
              ) : (
                <> The <span className="font-medium">{ignorePreset === 'roblox' ? 'Roblox' : 'UE5'}</span> <code>Ignored:</code> patterns are pre-populated on the new stream.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setConfirmOpen(false)}
              disabled={create.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => create.mutate()}
              disabled={create.isPending}
            >
              {create.isPending && <Spinner size="xs" />}
              Confirm + add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function autoParentAvailable(
  streams: Array<{ type: string; parent: string }> | undefined,
): boolean {
  if (!streams) return false;
  const mainlines = streams.filter(
    (s) => s.type === 'mainline' && (s.parent === '' || s.parent === 'none'),
  );
  return mainlines.length === 1;
}

// ---------------------------------------------------------------------------
// Recent admin actions section
// ---------------------------------------------------------------------------

function RecentAdminActionsSection() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin.actions'],
    queryFn: () => api.adminActions(50),
    refetchInterval: 30_000,
  });

  if (isLoading) return <Spinner />;
  if (error) {
    return (
      <Alert tone="destructive">
        <IconAlertCircle />
        <AlertDescription>{(error as Error).message}</AlertDescription>
      </Alert>
    );
  }
  if (!data || data.actions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No admin actions recorded yet.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          Last {data.actions.length} action{data.actions.length === 1 ? '' : 's'}
        </span>
        <button
          type="button"
          onClick={() => refetch()}
          className="text-xs text-theia-400 hover:underline"
        >
          Refresh
        </button>
      </div>
      <div className="rounded-md border border-neutral-800">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">When</TableHead>
              <TableHead className="w-[120px]">Actor</TableHead>
              <TableHead className="w-[140px]">Action</TableHead>
              <TableHead className="w-[100px]">Status</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.actions.map((a) => (
              <AdminActionRow key={a.id} action={a} />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AdminActionRow({ action }: { action: AdminAction }) {
  const tone: Tone =
    action.status === 'success'
      ? 'green'
      : action.status === 'failed'
        ? 'red'
        : action.status === 'partial'
          ? 'yellow'
          : 'blue';
  const summary = summarizeAction(action);
  return (
    <TableRow>
      <TableCell className="text-[11px] text-muted-foreground">
        <Tooltip label={new Date(action.startedAt).toLocaleString()}>
          <span className="cursor-help">
            {relativeTime(action.startedAt)}
          </span>
        </Tooltip>
      </TableCell>
      <TableCell className="font-mono text-[11px]">{action.actor}</TableCell>
      <TableCell className="text-[11px]">{action.actionType}</TableCell>
      <TableCell>
        <Badge tone={tone}>{action.status}</Badge>
      </TableCell>
      <TableCell className="text-[11px] text-muted-foreground">
        {summary}
        {action.error && (
          <div className="mt-1 text-red-300">{action.error}</div>
        )}
      </TableCell>
    </TableRow>
  );
}

function summarizeAction(a: AdminAction): string {
  if (a.actionType === 'depot.create') {
    const name = typeof a.payload.name === 'string' ? a.payload.name : '?';
    const depth =
      typeof a.payload.streamDepth === 'number' ? a.payload.streamDepth : 1;
    return `${name} (depth ${depth})`;
  }
  if (a.actionType === 'stream.create') {
    const depot = typeof a.payload.depot === 'string' ? a.payload.depot : '?';
    const name = typeof a.payload.name === 'string' ? a.payload.name : '?';
    const type = typeof a.payload.type === 'string' ? a.payload.type : '?';
    const populate = a.payload.populate === true ? ' + populate' : '';
    return `//${depot}/${name} (${type}${populate})`;
  }
  // Generic fallback — show keys.
  return Object.keys(a.payload).join(', ');
}

